'use client';

import {
  Box,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  TextField,
  CircularProgress,
  Tooltip,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { useState, useEffect } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useLanguage } from '../../../utils/i18n';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

export default function VehicleAssignTable({
  assignedVehicles = [],
  setAssignedVehicles,
  availableVehicles = [],
  isLoadingVehicles = false,
  isReadOnly = false,
  onSearchVehicle = () => {},
  onLoadMoreVehicles = () => {},
  tabMode = 'active',
  isEditMode = false
}) {
  const { t } = useLanguage();

  const [useDefaultForAll, setUseDefaultForAll] = useState(tabMode === 'history' ? false : !isEditMode);

  useEffect(() => {
    setUseDefaultForAll(tabMode === 'history' ? false : !isEditMode);
  }, [tabMode, isEditMode]);

  // Active => Today, History => Yesterday
  const defaultDateStr = tabMode === 'active' ? dayjs().format('YYYY-MM-DD') : dayjs().subtract(1, 'day').format('YYYY-MM-DD');

  const [shortcutDate, setShortcutDate] = useState(defaultDateStr);

  const isAllVehiclesSelected =
    Array.isArray(assignedVehicles) && availableVehicles.length > 0 && assignedVehicles.length === availableVehicles.length;

  const handleSelectAllChange = (checked) => {
    if (isReadOnly) return;
    if (checked) {
      const newAssignments = availableVehicles.map((v) => ({
        id: `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        vehicle: v,
        assign_date: useDefaultForAll ? defaultDateStr : shortcutDate
      }));
      setAssignedVehicles(newAssignments);
    } else {
      setAssignedVehicles([]);
    }
  };

  const handleShortcutDateChange = (v) => {
    if (isReadOnly) return;
    const dateStr = v ? v.format('YYYY-MM-DD') : null;
    setShortcutDate(dateStr);

    // Auto fill for existing rows
    const current = Array.isArray(assignedVehicles) ? assignedVehicles : [];
    if (current.length > 0) {
      setAssignedVehicles(current.map((item) => ({ ...item, assign_date: dateStr })));
    }
  };

  const handleAddVehicle = () => {
    if (isReadOnly) return;

    // Ensure the ID is truly unique by mixing timestamp and randomness
    const uniqueId = `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const current = Array.isArray(assignedVehicles) ? assignedVehicles : [];
    setAssignedVehicles([
      ...current,
      {
        id: uniqueId,
        vehicle: null,
        assign_date: useDefaultForAll ? defaultDateStr : shortcutDate
      }
    ]);
  };

  const handleRemoveVehicle = (id) => {
    if (isReadOnly) return;
    const current = Array.isArray(assignedVehicles) ? assignedVehicles : [];
    setAssignedVehicles(current.filter((v) => v.id !== id));
  };

  const handleChangeVehicle = (id, newVehicle) => {
    if (isReadOnly) return;
    const current = Array.isArray(assignedVehicles) ? assignedVehicles : [];
    const updated = current.map((v) => {
      if (v.id === id) {
        return { ...v, vehicle: newVehicle };
      }
      return v;
    });
    setAssignedVehicles(updated);
  };

  const handleChangeDate = (id, newDateStr, index) => {
    if (isReadOnly) return;
    const current = Array.isArray(assignedVehicles) ? assignedVehicles : [];
    const updated = current.map((v, i) => {
      if (v.id === id || i === index) {
        return { ...v, assign_date: newDateStr };
      }
      return v;
    });
    setAssignedVehicles(updated);
  };

  const getFilteredOptions = (currentSelectionId) => {
    const current = Array.isArray(assignedVehicles) ? assignedVehicles : [];
    const selectedVehicleIds = current
      .filter((v) => v.vehicle != null && v.id !== currentSelectionId)
      .map((v) => v.vehicle.id ?? v.vehicle.vehicle_id);

    return availableVehicles.filter((v) => {
      const vId = v.id ?? v.vehicle_id;
      return !selectedVehicleIds.includes(vId);
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#485D9A' }}>
              {t('masterRoute.assignedVehiclesList', 'Assigned Vehicles List')}
            </Typography>
            <Typography sx={{ color: '#6b7280', fontSize: 11, mb: 1 }}>
              {t(
                'masterRoute.assignedVehiclesTips',
                'Specify the vehicles that will operate on this route along with their effective date.'
              )}
            </Typography>
            {!isReadOnly && (
              <Box sx={{ display: 'flex', gap: 3, mb: 1, alignItems: 'center' }}>
                {tabMode === 'active' && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={useDefaultForAll}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setUseDefaultForAll(checked);
                          if (checked && !isReadOnly) {
                            const current = Array.isArray(assignedVehicles) ? assignedVehicles : [];
                            setAssignedVehicles(current.map((v) => ({ ...v, assign_date: defaultDateStr })));
                          }
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                        Current date for all vehicles
                      </Typography>
                    }
                  />
                )}

                {!useDefaultForAll && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {tabMode === 'history' && (
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                        Set backdate for all vehicles:
                      </Typography>
                    )}
                    <DatePicker
                      format="DD/MM/YYYY"
                      value={shortcutDate ? dayjs(shortcutDate) : null}
                      onChange={handleShortcutDateChange}
                      {...(tabMode === 'active' ? { minDate: dayjs().startOf('day') } : {})}
                      {...(tabMode === 'history' ? { maxDate: dayjs().subtract(1, 'day') } : {})}
                      slotProps={{
                        textField: {
                          size: 'small',
                          placeholder: 'Set date for all',
                          sx: { width: 140, bgcolor: '#fff' }
                        }
                      }}
                    />
                  </Box>
                )}

                <FormControlLabel
                  control={
                    <Checkbox size="small" checked={isAllVehiclesSelected} onChange={(e) => handleSelectAllChange(e.target.checked)} />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                      All Vehicle
                    </Typography>
                  }
                />
              </Box>
            )}
          </Box>
        </Box>

        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, mb: 2 }}>
          <Table sx={{ minWidth: 500 }} size="small">
            <TableHead sx={{ bgcolor: '#f9fafb' }}>
              <TableRow>
                <TableCell width="50px" align="center">
                  {t('masterRoute.no', 'No')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#4b5563' }}>{t('masterRoute.licensePlate', 'License Plate')}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#4b5563' }} width="200px">
                  {t('masterRoute.assignDate', 'Assignment Date')}
                </TableCell>
                <TableCell width="60px" align="center"></TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {!Array.isArray(assignedVehicles) || assignedVehicles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3, color: '#6b7280', fontStyle: 'italic' }}>
                    {t('masterRoute.noVehicleAssigned', 'No vehicles assigned for this route yet.')}
                  </TableCell>
                </TableRow>
              ) : (
                assignedVehicles.map((item, index) => {
                  const rowId = item.id || `api-row-${index}-${Math.random().toString(36).substr(2, 5)}`;
                  if (!item.id) item.id = rowId;

                  return (
                    <TableRow key={rowId} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell align="center">
                        <Typography variant="caption" fontWeight="bold">
                          {index + 1}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {(() => {
                          const getVehicleLabel = (option) => {
                            if (!option) return '';
                            const plate = option.vehicle_license_plate || option.license_plate || '';
                            const name = option.vehicle_name || option.name || '';
                            if (plate === name) return plate;
                            if (plate && name) return `${plate} - ${name}`;
                            return plate || name || t('masterRoute.unknownVehicle', 'Unknown Vehicle');
                          };

                          const selectedLabel = getVehicleLabel(item.vehicle);

                          return (
                            <Tooltip title={selectedLabel} placement="top" arrow disableHoverListener={!item.vehicle || isReadOnly}>
                              <span style={{ display: 'block' }}>
                                <Autocomplete
                                  size="small"
                                  options={getFilteredOptions(rowId)}
                                  disabled={isReadOnly}
                                  loading={isLoadingVehicles}
                                  getOptionLabel={getVehicleLabel}
                                  value={item.vehicle || null}
                                  onChange={(event, newValue) => handleChangeVehicle(rowId, newValue)}
                                  isOptionEqualToValue={(option, value) =>
                                    (option.id ?? option.vehicle_id) === (value.id ?? value.vehicle_id)
                                  }
                                  onInputChange={(event, newInputValue, reason) => {
                                    if (reason === 'input') onSearchVehicle(newInputValue);
                                    else if (reason === 'clear') onSearchVehicle('');
                                  }}
                                  ListboxProps={{
                                    onScroll: (event) => {
                                      const listboxNode = event.currentTarget;
                                      if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10) {
                                        onLoadMoreVehicles();
                                      }
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder={t('masterRoute.selectVehicle', 'Select Vehicle...')}
                                      sx={{ bgcolor: isReadOnly ? '#f9fafb' : '#fff' }}
                                      InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                          <>
                                            {isLoadingVehicles ? <CircularProgress color="inherit" size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                          </>
                                        )
                                      }}
                                    />
                                  )}
                                  renderOption={(props, option) => {
                                    const plate = option.vehicle_license_plate || option.license_plate || '-';
                                    const name = option.vehicle_name || option.name || '-';
                                    return (
                                      <li {...props} key={option.id ?? option.vehicle_id}>
                                        <Box>
                                          <Typography variant="body2" fontWeight="bold">
                                            {plate}
                                          </Typography>
                                          {plate !== name && (
                                            <Typography variant="caption" color="text.secondary">
                                              {name}
                                            </Typography>
                                          )}
                                        </Box>
                                      </li>
                                    );
                                  }}
                                />
                              </span>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>

                      <TableCell>
                        <DatePicker
                          format="DD/MM/YYYY"
                          value={item.assign_date ? dayjs(item.assign_date) : null}
                          disabled={isReadOnly}
                          readOnly={useDefaultForAll}
                          {...(tabMode === 'active' ? { minDate: dayjs().startOf('day') } : {})}
                          {...(tabMode === 'history' ? { maxDate: dayjs().subtract(1, 'day') } : {})}
                          onChange={(v) => {
                            const dateStr = v ? v.format('YYYY-MM-DD') : null;
                            handleChangeDate(rowId, dateStr, index);
                          }}
                          slotProps={{
                            textField: {
                              size: 'small',
                              fullWidth: true,
                              sx: { bgcolor: isReadOnly || useDefaultForAll ? '#f9fafb' : '#fff' }
                            }
                          }}
                        />
                      </TableCell>

                      <TableCell align="center">
                        <IconButton color="error" onClick={() => handleRemoveVehicle(rowId)} disabled={isReadOnly}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!isReadOnly && (
          <Button
            variant="outlined"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleAddVehicle}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#374151', borderColor: '#d1d5db', bgcolor: '#f9fafb' }}
          >
            {t('masterRoute.addVehicle', 'Add Vehicle')}
          </Button>
        )}
      </Box>
    </LocalizationProvider>
  );
}
